import React, { useState, useEffect, useCallback } from 'react';
import Cookies from 'js-cookie';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import CancelIcon from '@mui/icons-material/Cancel';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import UndoIcon from '@mui/icons-material/Undo';
import DescriptionIcon from '@mui/icons-material/Description';
import PersonIcon from '@mui/icons-material/Person';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

const API_BASE = 'https://system.sales-management.cloud/api/admin/invoice.php';

function AdminInvoice() {
    const [ invoices, setInvoices ] = useState([]);
    const [ summary, setSummary ] = useState([]);
    const [ loading, setLoading ] = useState(false);
    const [ error, setError ] = useState('');
    const [ success, setSuccess ] = useState('');
    const [ viewMode, setViewMode ] = useState('list'); // list or summary
    const [ selectedInvoice, setSelectedInvoice ] = useState(null);
    const [ showDetailModal, setShowDetailModal ] = useState(false);
    const [ filters, setFilters ] = useState({
        user_id: '',
        status: '',
        period: '',
        period_from: '',
        period_to: ''
    });

    // ステータス表示設定
    const statusConfig = {
        draft: { label: '下書き', color: 'bg-gray-100 text-gray-800', icon: DescriptionIcon },
        submitted: { label: '提出済', color: 'bg-blue-100 text-blue-800', icon: PendingIcon },
        approved: { label: '承認済', color: 'bg-green-100 text-green-800', icon: CheckCircleIcon },
        paid: { label: '支払済', color: 'bg-purple-100 text-purple-800', icon: AttachMoneyIcon },
        rejected: { label: '却下', color: 'bg-red-100 text-red-800', icon: CancelIcon },
        returned: { label: '差戻', color: 'bg-yellow-100 text-yellow-800', icon: UndoIcon }
    };

    // 請求書一覧を取得
    const fetchInvoices = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const token = Cookies.get('auth_token');
            if (!token) {
                setError('認証情報が見つかりません');
                return;
            }

            const params = new URLSearchParams();
            if (filters.user_id) params.append('user_id', filters.user_id);
            if (filters.status) params.append('status', filters.status);
            if (filters.period) params.append('period', filters.period);
            if (filters.period_from) params.append('period_from', filters.period_from);
            if (filters.period_to) params.append('period_to', filters.period_to);

            const response = await fetch(`${API_BASE}?${params.toString()}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            const data = await response.json();
            
            if (data.status === 'success') {
                setInvoices(data.data.invoices || []);
            } else {
                setError(data.message || '請求書の取得に失敗しました');
            }
        } catch (err) {
            console.error('Fetch error:', err);
            setError('通信エラーが発生しました');
        } finally {
            setLoading(false);
        }
    }, [ filters ]);

    // サマリーを取得
    const fetchSummary = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const token = Cookies.get('auth_token');
            if (!token) {
                setError('認証情報が見つかりません');
                setLoading(false);
                return;
            }

            const params = new URLSearchParams({ summary: '1', include_sales: '1' });
            // period指定がない場合、すべての月のデータを取得
            if (filters.period) params.append('period', filters.period);
            // user_id指定がある場合のみ追加
            if (filters.user_id) params.append('user_id', filters.user_id);

            console.log('Fetching summary with params:', params.toString());

            const response = await fetch(`${API_BASE}?${params.toString()}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            console.log('Summary response status:', response.status);
            const data = await response.json();
            
            console.log('Summary API Response:', data);
            if (data.status === 'success') {
                const rawSummary = data.data?.summary || [];
                console.log('Raw Summary:', rawSummary);
                console.log('First summary item detail:', rawSummary[ 0 ]);

                if (rawSummary.length === 0) {
                    console.warn('No summary data returned from API');
                    setSummary([]);
                    setLoading(false);
                    return;
                }

                // まず基本情報のみでグループ化（承認済み金額は後で計算）
                const processedSummary = rawSummary.map(item => ({
                    ...item,
                    approved_sales_items: [],
                    approved_invoice_ids: []
                }));

                console.log('Processed Summary (before approved calculation):', processedSummary);

                // ユーザーごと・月ごとにグループ化（同一ユーザーの同一月を合算）
                const userMonthlyGroups = {};
                processedSummary.forEach(item => {
                    const key = `${item.user_id}_${item.period}`;

                    if (!userMonthlyGroups[ key ]) {
                        // 新規ユーザー・月の組み合わせ
                        userMonthlyGroups[ key ] = {
                            user_id: item.user_id,
                            user_name: item.user_name,
                            period: item.period,
                            invoice_number: item.invoice_number,
                            amount: item.total_amount,
                            invoice_count: item.invoice_count,
                            sales_count: item.sales_count,
                            status_breakdown: { ...item.status_breakdown }
                        };
                    } else {
                        // 既存のユーザー・月の組み合わせに合算
                        userMonthlyGroups[ key ].amount += item.total_amount;
                        userMonthlyGroups[ key ].invoice_count += item.invoice_count;
                        userMonthlyGroups[ key ].sales_count += item.sales_count;

                        // ステータス内訳を合算
                        Object.keys(item.status_breakdown).forEach(status => {
                            userMonthlyGroups[ key ].status_breakdown[ status ] += item.status_breakdown[ status ];
                        });
                    }
                });

                console.log('User Monthly Groups (before approved calculation):', userMonthlyGroups);

                // 各ユーザー・月の組み合わせについて、承認済み金額を計算
                const approvedAmountPromises = Object.keys(userMonthlyGroups).map(async key => {
                    const item = userMonthlyGroups[ key ];
                    const approvedCount = (item.status_breakdown?.approved || 0) + (item.status_breakdown?.paid || 0);

                    let approvedAmount = 0;

                    if (approvedCount > 0) {
                        try {
                            const [ approvedRes, paidRes ] = await Promise.all([
                                fetch(`${API_BASE}?user_id=${item.user_id}&period=${item.period}&status=approved`, {
                                    headers: { 'Authorization': `Bearer ${token}` }
                                }),
                                fetch(`${API_BASE}?user_id=${item.user_id}&period=${item.period}&status=paid`, {
                                    headers: { 'Authorization': `Bearer ${token}` }
                                })
                            ]);

                            const approvedData = await approvedRes.json();
                            const paidData = await paidRes.json();

                            const approvedInvoices = [
                                ...(approvedData.data?.invoices || []),
                                ...(paidData.data?.invoices || [])
                            ];

                            // 重複する請求書を除外（invoice_idで重複チェック）
                            const uniqueInvoices = [];
                            const seenIds = new Set();
                            approvedInvoices.forEach(inv => {
                                if (!seenIds.has(inv.invoice_id)) {
                                    seenIds.add(inv.invoice_id);
                                    uniqueInvoices.push(inv);
                                }
                            });

                            // 承認済み/支払済み請求書の金額を合計
                            approvedAmount = uniqueInvoices.reduce((sum, inv) => sum + (inv.amount || 0), 0);

                            console.log(`User ${item.user_id}, Period ${item.period}:`, {
                                total_amount: item.amount,
                                approvedCount,
                                approvedInvoices: approvedInvoices.length,
                                uniqueInvoices: uniqueInvoices.length,
                                invoiceIds: uniqueInvoices.map(inv => inv.invoice_id),
                                calculated_approved_amount: approvedAmount
                            });
                        } catch (err) {
                            console.error('Error fetching approved invoices:', err);
                        }
                    }

                    return { key, approvedAmount };
                });

                const approvedAmounts = await Promise.all(approvedAmountPromises);

                // 承認済み金額をuserMonthlyGroupsに追加
                approvedAmounts.forEach(({ key, approvedAmount }) => {
                    userMonthlyGroups[ key ].approved_amount = approvedAmount;
                });

                console.log('User Monthly Groups (after approved calculation):', userMonthlyGroups);

                // 月ごとにグループ化
                const monthlyGroups = {};
                Object.values(userMonthlyGroups).forEach(item => {
                    const period = item.period;
                    if (!monthlyGroups[ period ]) {
                        monthlyGroups[ period ] = {
                            period: period,
                            users: [],
                            total_amount: 0,
                            approved_amount: 0,
                            invoice_count: 0,
                            sales_count: 0,
                            status_breakdown: {
                                draft: 0,
                                submitted: 0,
                                approved: 0,
                                paid: 0,
                                rejected: 0,
                                returned: 0
                            }
                        };
                    }

                    // ユーザー情報を追加
                    monthlyGroups[ period ].users.push({
                        user_id: item.user_id,
                        user_name: item.user_name,
                        invoice_number: item.invoice_number,
                        amount: item.amount,
                        approved_amount: item.approved_amount,
                        invoice_count: item.invoice_count,
                        sales_count: item.sales_count,
                        status_breakdown: item.status_breakdown
                    });

                    // 合計値を集計
                    monthlyGroups[ period ].total_amount += item.amount;
                    monthlyGroups[ period ].approved_amount += item.approved_amount;
                    monthlyGroups[ period ].invoice_count += item.invoice_count;
                    monthlyGroups[ period ].sales_count += item.sales_count;

                    // ステータスごとの件数を集計
                    Object.keys(item.status_breakdown).forEach(status => {
                        monthlyGroups[ period ].status_breakdown[ status ] += item.status_breakdown[ status ];
                    });
                });

                // オブジェクトを配列に変換し、期間でソート
                const monthlySummary = Object.values(monthlyGroups).sort((a, b) =>
                    b.period.localeCompare(a.period) // 新しい月が上に来るように降順
                );

                console.log('Monthly Summary:', monthlySummary);
                setSummary(monthlySummary);
            } else {
                setError(data.message || 'サマリーの取得に失敗しました');
            }
        } catch (err) {
            console.error('Fetch error:', err);
            setError('通信エラーが発生しました');
        } finally {
            setLoading(false);
        }
    }, [ filters ]);

    // 請求書詳細を取得
    const fetchInvoiceDetail = async (invoiceId) => {
        try {
            const token = Cookies.get('auth_token');
            if (!token) {
                setError('認証情報が見つかりません');
                return;
            }

            const response = await fetch(`${API_BASE}?id=${invoiceId}&include_sales=1`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            const data = await response.json();
            
            if (data.status === 'success') {
                setSelectedInvoice(data.data);
                setShowDetailModal(true);
            } else {
                setError(data.message || '請求書詳細の取得に失敗しました');
            }
        } catch (err) {
            console.error('Fetch detail error:', err);
            setError('通信エラーが発生しました');
        }
    };

    // 承認済み請求書の売上明細を取得（invoice_idから）
    const fetchApprovedInvoiceSales = async (invoiceIds) => {
        const token = Cookies.get('auth_token');
        if (!token || !invoiceIds || invoiceIds.length === 0) {
            return [];
        }

        try {
            const salesPromises = invoiceIds.map(id =>
                fetch(`${API_BASE}?id=${id}&include_sales=1`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                }).then(async res => {
                    const data = await res.json();
                    return data;
                })
            );

            const results = await Promise.all(salesPromises);
            const allSales = [];

            results.forEach(data => {
                if (data.status === 'success' && data.data) {
                    if (data.data.sales_items && data.data.sales_items.length > 0) {
                        data.data.sales_items.forEach(sale => {
                            if (!allSales.find(s => s.sale_id === sale.sale_id)) {
                                allSales.push(sale);
                            }
                        });
                    } else {
                        // sales_itemsが空の場合、請求書自体の情報から1つの明細を作成
                        allSales.push({
                            sale_id: `invoice_${data.data.invoice_id}`,
                            sale_date: data.data.period,
                            company_name: '-',
                            client_name: '-',
                            product_name: '-',
                            final_amount: data.data.amount || 0,
                            notes: data.data.notes || '請求書明細',
                            status: 'completed'
                        });
                    }
                }
            });

            return allSales;
        } catch (err) {
            console.error('Error fetching invoice sales:', err);
            return [];
        }
    };

    // 承認済み請求書の売上明細を取得（フォールバック）
    const fetchApprovedSalesForPeriod = async (userId, period) => {
        const token = Cookies.get('auth_token');
        if (!token) return [];

        try {
            // 承認済みと支払済みの請求書を取得
            const [ approvedRes, paidRes ] = await Promise.all([
                fetch(`${API_BASE}?user_id=${userId}&period=${period}&status=approved`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                }),
                fetch(`${API_BASE}?user_id=${userId}&period=${period}&status=paid`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                })
            ]);

            const approvedData = await approvedRes.json();
            const paidData = await paidRes.json();

            const allInvoices = [
                ...(approvedData.data?.invoices || []),
                ...(paidData.data?.invoices || [])
            ];

            console.log('Approved/Paid invoices for user', userId, period, ':', allInvoices);

            // 各請求書の詳細（売上明細付き）を取得
            const salesPromises = allInvoices.map(inv =>
                fetch(`${API_BASE}?id=${inv.invoice_id}&include_sales=1`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                }).then(res => res.json())
            );

            const results = await Promise.all(salesPromises);
            const allSales = [];

            console.log('Invoice detail results:', results);

            results.forEach((data, index) => {
                console.log(`Processing result ${index}:`, data);
                if (data.status === 'success' && data.data) {
                    if (data.data.sales_items && data.data.sales_items.length > 0) {
                        console.log('Invoice detail sales:', data.data.sales_items);
                        data.data.sales_items.forEach(sale => {
                            if (!allSales.find(s => s.sale_id === sale.sale_id)) {
                                allSales.push(sale);
                            }
                        });
                    } else {
                        // sales_itemsが空の場合、請求書自体の情報から1つの明細を作成
                        console.log('No sales_items, creating from invoice data:', data.data);
                        const invoiceDetail = {
                            sale_id: `invoice_${data.data.invoice_id}`,
                            sale_date: data.data.period,
                            company_name: '-',
                            client_name: '-',
                            product_name: '-',
                            final_amount: data.data.amount || 0,
                            notes: data.data.notes || '請求書明細',
                            status: 'completed'
                        };
                        console.log('Created invoice detail:', invoiceDetail);
                        allSales.push(invoiceDetail);
                    }
                }
            });

            console.log('Final allSales:', allSales);
            return allSales;
        } catch (err) {
            console.error('Error fetching approved sales:', err);
            return [];
        }
    };

    // 適格請求書PDF生成
    const generateInvoicePDF = async (summaryItem) => {
        console.log('=== PDF Generation Start ===');
        console.log('Summary item:', summaryItem);
        console.log('approved_sales_items:', summaryItem.approved_sales_items);
        console.log('approved_invoice_ids:', summaryItem.approved_invoice_ids);
        console.log('status_breakdown:', summaryItem.status_breakdown);

        // 承認済み/支払済みの請求書に含まれる売上明細を取得
        let approvedSales = summaryItem.approved_sales_items || [];
        console.log('Initial approvedSales length:', approvedSales.length);

        // もし売上明細が空の場合、承認済み請求書から個別に取得
        if (approvedSales.length === 0) {
            console.log('No approved_sales_items, trying alternative methods...');

            if (summaryItem.approved_invoice_ids && summaryItem.approved_invoice_ids.length > 0) {
                console.log('Method 1: Fetching sales from approved_invoice_ids:', summaryItem.approved_invoice_ids);
                approvedSales = await fetchApprovedInvoiceSales(summaryItem.approved_invoice_ids);
                console.log('Method 1 result:', approvedSales.length, 'sales items');
            } else {
                // invoice_idがない場合、user_idとperiodで直接取得
                console.log('Method 2: Fetching sales for user/period:', summaryItem.user_id, summaryItem.period);
                approvedSales = await fetchApprovedSalesForPeriod(summaryItem.user_id, summaryItem.period);
                console.log('Method 2 result:', approvedSales.length, 'sales items');
            }
            console.log('Fetched approved sales:', approvedSales);
        }

        console.log('Final approved sales count:', approvedSales.length);
        console.log('Final approved sales items:', approvedSales);

        const companyInfo = {
            name: `${summaryItem.user_name}`,
            registrationNumber: `T${summaryItem.invoice_number}`,
            representative: `${summaryItem.user_name}`
        };

        // PDFを新しいウィンドウで開く
        const printWindow = window.open('', '_blank');
        if (!printWindow) {
            alert('ポップアップがブロックされました。ポップアップを許可してください。');
            return;
        }

        const today = new Date().toLocaleDateString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit' });

        // 翌月の10日を計算
        const [year, month] = summaryItem.period.split('-');
        let nextMonth = parseInt(month) + 1;
        let nextYear = parseInt(year);
        
        if (nextMonth > 12) {
            nextMonth = 1;
            nextYear = nextYear + 1;
        }
        
        const paymentDueDate = `${nextYear}-${String(nextMonth).padStart(2, '0')}-10`;

        // 承認済み売上の合計計算（APIから取得する金額は税込）
        const totalWithTax = approvedSales.reduce((sum, sale) => sum + (sale.final_amount || 0), 0);
        // 税込金額から税抜金額と税額を逆算（税率10%）
        const subtotal = Math.floor(totalWithTax / 1.1);
        const tax = totalWithTax - subtotal;
        const total = totalWithTax;

        console.log('PDF amounts - Total with tax:', totalWithTax, 'Subtotal:', subtotal, 'Tax:', tax);

        const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>適格請求書 - ${summaryItem.user_name} ${summaryItem.period}</title>
        <style>
          @media print {
            @page { size: A4; margin: 15mm; }
          }
          body {
            font-family: 'MS Gothic', 'Yu Gothic', sans-serif;
            margin: 0;
            padding: 20px;
            font-size: 12px;
            line-height: 1.6;
          }
          .container {
            max-width: 800px;
            margin: 0 auto;
          }
          .header {
            text-align: center;
            margin-bottom: 30px;
            border-bottom: 2px solid #333;
            padding-bottom: 10px;
          }
          .header h1 {
            font-size: 24px;
            margin: 0;
          }
          .invoice-info {
            display: flex;
            justify-content: space-between;
            margin-bottom: 30px;
          }
          .company-info {
            text-align: right;
            font-size: 11px;
            line-height: 1.8;
          }
          .client-info {
            font-size: 14px;
          }
          .registration-number {
            font-weight: bold;
            color: #d32f2f;
            margin-top: 5px;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin: 20px 0;
          }
          th, td {
            border: 1px solid #333;
            padding: 8px;
            text-align: left;
          }
          th {
            background-color: #f0f0f0;
            font-weight: bold;
          }
          .text-right {
            text-align: right;
          }
          .text-center {
            text-align: center;
          }
          .total-section {
            margin-top: 30px;
            float: right;
            width: 300px;
          }
          .total-row {
            display: flex;
            justify-content: space-between;
            padding: 8px;
            border-bottom: 1px solid #ddd;
          }
          .total-row.grand-total {
            font-weight: bold;
            font-size: 16px;
            background-color: #f0f0f0;
            border: 2px solid #333;
          }
          .notes {
            clear: both;
            margin-top: 100px;
            padding-top: 20px;
            border-top: 1px solid #ddd;
          }
          .footer {
            margin-top: 30px;
            text-align: center;
            font-size: 10px;
            color: #666;
          }
          .status-badges {
            margin: 10px 0;
          }
          .badge {
            display: inline-block;
            padding: 4px 8px;
            margin: 2px;
            border-radius: 4px;
            font-size: 11px;
          }
          .badge-approved { background-color: #c8e6c9; color: #2e7d32; }
          .badge-paid { background-color: #ce93d8; color: #6a1b9a; }
          .badge-submitted { background-color: #bbdefb; color: #1565c0; }
          .badge-rejected { background-color: #ffcdd2; color: #c62828; }
          .badge-returned { background-color: #fff9c4; color: #f57f17; }
        </style>
    </head>
    <body>
        <div class="container">
          <div class="header">
            <h1>適格請求書（インボイス）</h1>
            <p style="margin: 5px 0;">発行日: ${today}</p>
          </div>

          <div class="invoice-info">
            <div class="client-info">
              <div style="font-size: 16px; font-weight: bold; margin-bottom: 10px;">
                株式会社PieceValue 御中
              </div>
              <div style="margin-top: 20px;">
                <div>請求月: ${summaryItem.period}</div>
                <div>請求件数: ${approvedSales.length}件</div>
                <div>請求金額: ¥${summaryItem.approved_amount.toLocaleString()}</div>
              </div>
            </div>

            <div class="company-info">
              <div style="font-weight: bold; font-size: 14px;">${companyInfo.name}</div>
              <div class="registration-number">登録番号: ${companyInfo.registrationNumber}</div>
              <div>${companyInfo.representative}</div>
            </div>
          </div>

          <h3>明細</h3>
          <table>
            <thead>
              <tr>
                <th class="text-center" style="width: 100px;">取引日</th>
                <th>概要</th>
                <th class="text-center" style="width: 60px;">件数</th>
                <th class="text-right" style="width: 120px;">金額（税抜）</th>
              </tr>
            </thead>
            <tbody>
              ${approvedSales.length > 0
                ? approvedSales.map(sale => {
                    // 概要: 売上のnotesフィールドを表示
                    const description = sale.notes || '-';
                    // APIの金額は税込なので、税抜金額を計算（税率10%）
                    const amountWithTax = sale.final_amount || 0;
                    const amountExcludingTax = Math.floor(amountWithTax / 1.1);

                    return `
                    <tr>
                      <td class="text-center">${new Date(sale.sale_date).toLocaleDateString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit' })}</td>
                      <td>${description}</td>
                      <td class="text-center">1</td>
                      <td class="text-right">¥${amountExcludingTax.toLocaleString()}</td>
                    </tr>
                      `;
                }).join('')
                : '<tr><td colspan="4" class="text-center">承認済み売上明細がありません</td></tr>'
            }
            </tbody>
          </table>

          <div class="total-section">
            <div class="total-row">
              <span>小計（税抜）</span>
              <span>¥${subtotal.toLocaleString()}</span>
            </div>
            <div class="total-row">
              <span>消費税（10%）</span>
              <span>¥${tax.toLocaleString()}</span>
            </div>
            <div class="total-row grand-total">
              <span>合計（税込）</span>
              <span>¥${total.toLocaleString()}</span>
            </div>
          </div>

          <div class="notes">
            <h4>備考</h4>
            <p>お支払期限: ${paymentDueDate}</p>
            <p>明細には小数点以下による丸め誤差が含まれる場合があります。</p>
          </div>

          <div class="footer">
            <p>このPDFは管理画面から自動生成されました。</p>
          </div>
        </div>

        <script>
          window.onload = function() {
            window.print();
          };
        </script>
      </body>
      </html>
    `;

        printWindow.document.write(htmlContent);
        printWindow.document.close();
    };

    // ステータス更新
    const updateInvoiceStatus = async (invoiceId, newStatus, adminMemo = '') => {
        try {
            const token = Cookies.get('auth_token');
            if (!token) {
                setError('認証情報が見つかりません');
                return;
            }

            const response = await fetch(API_BASE, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    invoice_id: invoiceId,
                    new_status: newStatus,
                    admin_memo: adminMemo
                })
            });

            const data = await response.json();
            
            if (data.status === 'success') {
                setSuccess(`ステータスを「${statusConfig[ newStatus ].label}」に更新しました`);
                setShowDetailModal(false);
                setSelectedInvoice(null);

                // データを再取得
                if (viewMode === 'list') {
                    fetchInvoices();
                } else {
                    fetchSummary();
                }
            } else {
                setError(data.message || 'ステータスの更新に失敗しました');
            }
        } catch (err) {
            console.error('Update error:', err);
            setError('通信エラーが発生しました');
        }
    };

    useEffect(() => {
        if (viewMode === 'list') {
            fetchInvoices();
        } else {
            fetchSummary();
        }
    }, [ viewMode, fetchInvoices, fetchSummary ]);

    // 金額フォーマット
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('ja-JP', {
            style: 'currency',
            currency: 'JPY'
        }).format(amount);
    };

    // 日付フォーマット
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('ja-JP', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        });
    };

    return (
        <div className="min-h-screen bg-gray-50 p-4 md:p-6">
            <div className="max-w-7xl mx-auto">
                {/* ヘッダー */}
                <div className="mb-6">
                    <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">請求書管理</h1>
                    <p className="text-sm text-gray-600">業務委託者の請求書を確認・管理します</p>
                </div>

                {/* 成功・エラーメッセージ */}
                {success && (
                    <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-md">
                        <p className="text-sm text-green-800">{success}</p>
                    </div>
                )}
                {error && (
                    <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
                        <p className="text-sm text-red-800">{error}</p>
                    </div>
                )}

                {/* 表示モード切替 */}
                <div className="mb-6 flex gap-2">
                    <button
                        onClick={() => setViewMode('list')}
                        className={`px-4 py-2 rounded-md font-medium transition-colors ${viewMode === 'list'
                            ? 'bg-blue-600 text-white'
                            : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                            }`}
                    >
                        一覧表示
                    </button>
                    <button
                        onClick={() => setViewMode('summary')}
                        className={`px-4 py-2 rounded-md font-medium transition-colors ${viewMode === 'summary'
                            ? 'bg-blue-600 text-white'
                            : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                            }`}
                    >
                        サマリー表示
                    </button>
                </div>

                {/* フィルター */}
                <div className="bg-white rounded-lg shadow-md p-4 md:p-6 mb-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">フィルター</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">ユーザーID</label>
                            <input
                                type="number"
                                value={filters.user_id}
                                onChange={(e) => setFilters({ ...filters, user_id: e.target.value })}
                                placeholder="ユーザーID"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">ステータス</label>
                            <select
                                value={filters.status}
                                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="">すべて</option>
                                <option value="draft">下書き</option>
                                <option value="submitted">提出済</option>
                                <option value="approved">承認済</option>
                                <option value="paid">支払済</option>
                                <option value="rejected">却下</option>
                                <option value="returned">差戻</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">期間 (YYYY-MM)</label>
                            <input
                                type="month"
                                value={filters.period}
                                onChange={(e) => setFilters({ ...filters, period: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                    </div>
                    <div className="mt-4 flex gap-2">
                        <button
                            onClick={() => viewMode === 'list' ? fetchInvoices() : fetchSummary()}
                            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium"
                        >
                            検索
                        </button>
                        <button
                            onClick={() => {
                                setFilters({ user_id: '', status: '', period: '', period_from: '', period_to: '' });
                                setSuccess('');
                                setError('');
                            }}
                            className="px-6 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors font-medium"
                        >
                            クリア
                        </button>
                    </div>
                </div>

                {/* 一覧表示 */}
                {viewMode === 'list' && (
                    <div className="bg-white rounded-lg shadow-md overflow-hidden">
                        {loading ? (
                            <div className="p-8 text-center text-gray-500">読み込み中...</div>
                        ) : invoices.length === 0 ? (
                            <div className="p-8 text-center text-gray-500">請求書がありません</div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ユーザー</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">期間</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">金額</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ステータス</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">作成日</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">操作</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {invoices.map((invoice) => {
                                            const StatusIcon = statusConfig[ invoice.status ]?.icon || DescriptionIcon;
                                            return (
                                                <tr key={invoice.invoice_id} className="hover:bg-gray-50">
                                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                                        {invoice.invoice_id}
                                                    </td>
                                                    <td className="px-4 py-3 whitespace-nowrap">
                                                        <div className="flex items-center">
                                                            <PersonIcon className="text-gray-400 mr-2" fontSize="small" />
                                                            <div>
                                                                <div className="text-sm font-medium text-gray-900">{invoice.user_name}</div>
                                                                <div className="text-xs text-gray-500">ID: {invoice.user_id}</div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                                        {invoice.period}
                                                    </td>
                                                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                                                        {formatCurrency(invoice.amount)}
                                                    </td>
                                                    <td className="px-4 py-3 whitespace-nowrap">
                                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusConfig[ invoice.status ]?.color}`}>
                                                            <StatusIcon fontSize="small" className="mr-1" />
                                                            {statusConfig[ invoice.status ]?.label}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                                        {formatDate(invoice.created_at)}
                                                    </td>
                                                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                                                        <button
                                                            onClick={() => fetchInvoiceDetail(invoice.invoice_id)}
                                                            className="text-blue-600 hover:text-blue-800 font-medium"
                                                        >
                                                            詳細
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}

                {/* サマリー表示 */}
                {viewMode === 'summary' && (
                    <div className="space-y-4">
                        {loading ? (
                            <div className="bg-white rounded-lg shadow-md p-8 text-center text-gray-500">
                                読み込み中...
                            </div>
                        ) : summary.length === 0 ? (
                            <div className="bg-white rounded-lg shadow-md p-8 text-center text-gray-500">
                                サマリーがありません
                            </div>
                        ) : (
                            summary.map((monthData) => (
                                <div key={monthData.period} className="bg-white rounded-lg shadow-md p-6">
                                    {/* 月ごとのヘッダー */}
                                    <div className="border-b-2 border-blue-500 pb-4 mb-6">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <h2 className="text-2xl font-bold text-gray-900">{monthData.period}</h2>
                                                <p className="text-sm text-gray-600 mt-1">
                                                    計上者: {monthData.users.length}名 | 請求書: {monthData.invoice_count}件 | 売上: {monthData.sales_count}件
                                                </p>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-xs text-gray-500">月合計（承認済み）</p>
                                                <p className="text-3xl font-bold text-blue-600">{formatCurrency(monthData.approved_amount)}</p>
                                                <p className="text-sm text-gray-600 mt-1">総額: {formatCurrency(monthData.total_amount)}</p>
                                            </div>
                                        </div>

                                        {/* 月全体のステータス内訳 */}
                                        <div className="mt-4 flex flex-wrap gap-2">
                                            {Object.entries(monthData.status_breakdown)
                                                .sort(([ statusA ], [ statusB ]) => {
                                                    const order = [ 'approved', 'paid', 'submitted', 'rejected', 'returned', 'draft' ];
                                                    return order.indexOf(statusA) - order.indexOf(statusB);
                                                })
                                                .map(([ status, count ]) => {
                                                    if (count === 0) return null;
                                                    const StatusIcon = statusConfig[ status ]?.icon || DescriptionIcon;
                                                    return (
                                                        <span key={status} className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${statusConfig[ status ]?.color}`}>
                                                            <StatusIcon fontSize="small" className="mr-1" />
                                                            {statusConfig[ status ]?.label}: {count}件
                                                        </span>
                                                    );
                                                })}
                                        </div>
                                    </div>

                                    {/* 各ユーザーの詳細 */}
                                    <div className="space-y-4">
                                        {monthData.users.map((user) => (
                                            <div key={`${user.user_id}-${monthData.period}`} className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors">
                                                <div className="flex items-center justify-between mb-3">
                                                    <div className="flex items-center">
                                                        <PersonIcon className="text-blue-600 mr-2" />
                                                        <div>
                                                            <h3 className="text-lg font-semibold text-gray-900">{user.user_name}</h3>
                                                            <p className="text-sm text-gray-500">ID: {user.user_id}</p>
                                                        </div>
                                                    </div>
                                                    <div className="text-right">
                                                        <p className="text-xl font-bold text-blue-600">{formatCurrency(user.approved_amount)}</p>
                                                        <p className="text-xs text-gray-500">承認済み換算額</p>
                                                        <p className="text-sm text-gray-600 mt-1">総額: {formatCurrency(user.amount)}</p>
                                                        <p className="text-xs text-gray-500">請求書: {user.invoice_count}件 | 売上: {user.sales_count}件</p>
                                                    </div>
                                                </div>

                                                {/* ユーザーごとのステータス内訳 */}
                                                <div className="mb-3 flex flex-wrap gap-2">
                                                    {Object.entries(user.status_breakdown)
                                                        .sort(([ statusA ], [ statusB ]) => {
                                                            const order = [ 'approved', 'paid', 'submitted', 'rejected', 'returned', 'draft' ];
                                                            return order.indexOf(statusA) - order.indexOf(statusB);
                                                        })
                                                        .map(([ status, count ]) => {
                                                            if (count === 0) return null;
                                                            const StatusIcon = statusConfig[ status ]?.icon || DescriptionIcon;
                                                            return (
                                                                <span key={status} className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusConfig[ status ]?.color}`}>
                                                                    <StatusIcon fontSize="small" className="mr-1" style={{ fontSize: '14px' }} />
                                                                    {statusConfig[ status ]?.label}: {count}件
                                                                </span>
                                                            );
                                                        })}
                                                </div>

                                                {/* PDFボタン */}
                                                <div>
                                                    <button
                                                        onClick={() => generateInvoicePDF({
                                                            user_id: user.user_id,
                                                            user_name: user.user_name,
                                                            period: monthData.period,
                                                            invoice_number: user.invoice_number,
                                                            approved_amount: user.approved_amount,
                                                            total_amount: user.amount,
                                                            status_breakdown: user.status_breakdown
                                                        })}
                                                        className="inline-flex items-center px-3 py-1.5 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors font-medium text-sm"
                                                    >
                                                        <PictureAsPdfIcon className="mr-1" fontSize="small" />
                                                        適格請求書をPDF出力
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                )}

                {/* 詳細モーダル */}
                {showDetailModal && selectedInvoice && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex justify-between items-center">
                                <h2 className="text-xl font-bold text-gray-900">請求書詳細</h2>
                                <button
                                    onClick={() => {
                                        setShowDetailModal(false);
                                        setSelectedInvoice(null);
                                    }}
                                    className="text-gray-400 hover:text-gray-600"
                                >
                                    ✕
                                </button>
                            </div>

                            <div className="p-6 space-y-6">
                                {/* 基本情報 */}
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-500">請求書ID</label>
                                        <p className="text-lg font-semibold text-gray-900">{selectedInvoice.invoice_id}</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-500">ユーザー</label>
                                        <p className="text-lg font-semibold text-gray-900">{selectedInvoice.user_name}</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-500">期間</label>
                                        <p className="text-lg font-semibold text-gray-900">{selectedInvoice.period}</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-500">金額</label>
                                        <p className="text-2xl font-bold text-blue-600">{formatCurrency(selectedInvoice.amount)}</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-500">ステータス</label>
                                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${statusConfig[ selectedInvoice.status ]?.color}`}>
                                            {statusConfig[ selectedInvoice.status ]?.label}
                                        </span>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-500">作成日</label>
                                        <p className="text-lg text-gray-900">{formatDate(selectedInvoice.created_at)}</p>
                                    </div>
                                </div>

                                {/* 備考 */}
                                {selectedInvoice.notes && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-500 mb-1">備考</label>
                                        <p className="text-gray-900 bg-gray-50 p-3 rounded-md">{selectedInvoice.notes}</p>
                                    </div>
                                )}

                                {/* 管理者メモ */}
                                {selectedInvoice.admin_memo && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-500 mb-1">管理者メモ</label>
                                        <p className="text-gray-900 bg-yellow-50 p-3 rounded-md border border-yellow-200">{selectedInvoice.admin_memo}</p>
                                    </div>
                                )}

                                {/* 売上明細 */}
                                {selectedInvoice.sales_items && selectedInvoice.sales_items.length > 0 && (
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-900 mb-3">売上明細</h3>
                                        <div className="overflow-x-auto">
                                            <table className="min-w-full text-sm border border-gray-200">
                                                <thead className="bg-gray-50">
                                                    <tr>
                                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 border-b">日付</th>
                                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 border-b">会社名</th>
                                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 border-b">顧客名</th>
                                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 border-b">商品</th>
                                                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 border-b">金額</th>
                                                        <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 border-b">ステータス</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-gray-200">
                                                    {selectedInvoice.sales_items.map((sale) => (
                                                        <tr key={sale.sale_id}>
                                                            <td className="px-3 py-2 whitespace-nowrap">{formatDate(sale.sale_date)}</td>
                                                            <td className="px-3 py-2">{sale.company_name}</td>
                                                            <td className="px-3 py-2">{sale.client_name}</td>
                                                            <td className="px-3 py-2">{sale.product_name}</td>
                                                            <td className="px-3 py-2 text-right font-medium">{formatCurrency(sale.final_amount)}</td>
                                                            <td className="px-3 py-2 text-center">
                                                                <span className={`px-2 py-1 rounded text-xs ${sale.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                                                                    }`}>
                                                                    {sale.status}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}

                                {/* ステータス変更ボタン */}
                                {selectedInvoice.status === 'submitted' && (
                                    <div className="border-t pt-6">
                                        <h3 className="text-lg font-semibold text-gray-900 mb-4">ステータス変更</h3>
                                        <div className="flex flex-wrap gap-3">
                                            <button
                                                onClick={() => {
                                                    const memo = prompt('管理者メモ（任意）');
                                                    if (memo !== null) {
                                                        updateInvoiceStatus(selectedInvoice.invoice_id, 'approved', memo);
                                                    }
                                                }}
                                                className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                                            >
                                                <CheckCircleIcon className="mr-2" fontSize="small" />
                                                承認
                                            </button>
                                            <button
                                                onClick={() => {
                                                    const memo = prompt('差戻理由を入力してください');
                                                    if (memo) {
                                                        updateInvoiceStatus(selectedInvoice.invoice_id, 'returned', memo);
                                                    }
                                                }}
                                                className="flex items-center px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 transition-colors"
                                            >
                                                <UndoIcon className="mr-2" fontSize="small" />
                                                差戻
                                            </button>
                                            <button
                                                onClick={() => {
                                                    const memo = prompt('却下理由を入力してください');
                                                    if (memo) {
                                                        updateInvoiceStatus(selectedInvoice.invoice_id, 'rejected', memo);
                                                    }
                                                }}
                                                className="flex items-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                                            >
                                                <CancelIcon className="mr-2" fontSize="small" />
                                                却下
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {selectedInvoice.status === 'approved' && (
                                    <div className="border-t pt-6">
                                        <h3 className="text-lg font-semibold text-gray-900 mb-4">ステータス変更</h3>
                                        <div className="flex flex-wrap gap-3">
                                            <button
                                                onClick={() => {
                                                    const memo = prompt('支払完了メモ（任意）');
                                                    if (memo !== null) {
                                                        updateInvoiceStatus(selectedInvoice.invoice_id, 'paid', memo);
                                                    }
                                                }}
                                                className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
                                            >
                                                <AttachMoneyIcon className="mr-2" fontSize="small" />
                                                支払完了
                                            </button>
                                            <button
                                                onClick={() => {
                                                    const memo = prompt('差戻理由を入力してください');
                                                    if (memo) {
                                                        updateInvoiceStatus(selectedInvoice.invoice_id, 'returned', memo);
                                                    }
                                                }}
                                                className="flex items-center px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 transition-colors"
                                            >
                                                <UndoIcon className="mr-2" fontSize="small" />
                                                差戻
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default AdminInvoice;
